import "./App.css";
import { Suspense, useEffect } from "react";
import { lazyRetry } from "./lib/lazyRetry";
import AppErrorBoundary from "./components/AppErrorBoundary";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import Header from "./components/Header";
import RequireIdentity from "./components/RequireIdentity";
import LearnerIdPrompt from "./components/LearnerIdPrompt";
import ScrollToTop from "./components/ScrollToTop";
import { LangProvider } from "./lib/lang";
import { warmTopEndpoints } from "./lib/api";
// Landing was retired Feb 2026 — replaced by the MaldekHome homepage.
// (HomeConsulting was dropped from the eager bundle in the 2026-07 speed pass.)
import MaldekHome from "./pages/MaldekHome"; // New philosophy-first homepage.
const ConsultingPage = lazyRetry(() => import("./pages/ConsultingPage"));
const ClinicalReasoningPlatformPage = lazyRetry(() => import("./pages/ClinicalReasoningPlatformPage"));
const SystemOverviewPage = lazyRetry(() => import("./pages/SystemOverviewPage"));
const CommunityHealthPage = lazyRetry(() => import("./pages/CommunityHealthPage"));
const MysteryCase = lazyRetry(() => import("./pages/MysteryCase"));
const MysteryHub = lazyRetry(() => import("./pages/MysteryHub"));
const FacultyAnalytics = lazyRetry(() => import("./pages/FacultyAnalytics"));
const ThemeOptions = lazyRetry(() => import("./pages/ThemeOptions"));
const ModulesDirectory = lazyRetry(() => import("./pages/ModulesDirectory"));
const CaseStudiesPage = lazyRetry(() => import("./pages/CaseStudiesPage"));
const MaldekAdminDashboard = lazyRetry(() => import("./pages/MaldekAdminDashboard"));
const ClinicalContentControl = lazyRetry(() => import("./pages/ClinicalContentControl"));
const AboutPage = lazyRetry(() => import("./pages/AboutPage"));
const ContactPage = lazyRetry(() => import("./pages/ContactPage"));
const Landing = lazyRetry(() => import("./pages/Landing")); // Legacy founder dashboard only.
const LegacyLanding = lazyRetry(() => import("./pages/LegacyLanding"));
const WorkforcePipeline = lazyRetry(() => import("./pages/WorkforcePipeline"));
const Cme = lazyRetry(() => import("./pages/Cme"));
const BusinessPlan = lazyRetry(() => import("./pages/BusinessPlan"));
// Every other route is lazy-loaded so the main bundle only ships the
// landing + framework code. Each route becomes its own ~30-200 KB
// chunk that downloads on demand. Drops initial JS by ~60%.
const SimulatorHub = lazyRetry(() => import("./pages/SimulatorHub"));
const TimeMachine = lazyRetry(() => import("./pages/TimeMachine"));
const Patient = lazyRetry(() => import("./pages/Patient"));
const PracticeVisit = lazyRetry(() => import("./pages/PracticeVisit"));
const Nclex = lazyRetry(() => import("./pages/Nclex"));
const NclexDashboard = lazyRetry(() => import("./pages/NclexDashboard"));
const NclexAdmin = lazyRetry(() => import("./pages/NclexAdmin"));
const MasterDashboard = lazyRetry(() => import("./pages/MasterDashboard"));
const ForInstitutions = lazyRetry(() => import("./pages/ForInstitutions"));
const SafetyNet = lazyRetry(() => import("./pages/SafetyNet"));
const Walkthrough = lazyRetry(() => import("./pages/Walkthrough"));
const Symptoms = lazyRetry(() => import("./pages/Symptoms"));
const Therapeutics = lazyRetry(() => import("./pages/Therapeutics"));
const Reasoning = lazyRetry(() => import("./pages/Reasoning"));
const Start = lazyRetry(() => import("./pages/Start"));
// Results.jsx was retired Feb 2026 — analytics lives at /reasoning-pilot.
// The redirect at the route level preserves any external bookmarks.
const Results = lazyRetry(() => Promise.resolve({ default: () => null }));
// Run-Shift retired Feb 23, 2026 per founder direction — route now
// redirects to the dashboard so any external links keep working.
const Shift = lazyRetry(() => Promise.resolve({ default: () => null }));
const Login = lazyRetry(() => import("./pages/Login"));
const VerifyEmail = lazyRetry(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const AcceptInvite = lazyRetry(() => import("./pages/AcceptInvite"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Cohorts = lazyRetry(() => import("./pages/Cohorts"));
const CohortDetail = lazyRetry(() => import("./pages/CohortDetail"));
const AuthCallback = lazyRetry(() => import("./pages/AuthCallback"));
const ForHospitals = lazyRetry(() => import("./pages/ForHospitals"));
const ForSchools = lazyRetry(() => import("./pages/ForSchools"));
const HowItWorks = lazyRetry(() => import("./pages/HowItWorks"));
const Concept = lazyRetry(() => import("./pages/Concept"));
const ReasoningEngine = lazyRetry(() => import("./pages/ReasoningEngine"));
const Pediatrics = lazyRetry(() => import("./pages/Pediatrics"));
const PediatricCondition = lazyRetry(() => import("./pages/PediatricCondition"));
const CompareConditions = lazyRetry(() => import("./pages/CompareConditions"));
const CatalogHome = lazyRetry(() => import("./pages/CatalogHome"));
const ClinicalReasoningIntro = lazyRetry(() => import("./pages/ClinicalReasoningIntro"));
const ExplainShortLayouts = lazyRetry(() => import("./pages/ExplainShortLayouts"));
const ExplainLongLayouts = lazyRetry(() => import("./pages/ExplainLongLayouts"));
const CommunityLibraryLayouts = lazyRetry(() => import("./pages/CommunityLibraryLayouts"));
const ImagingLayouts = lazyRetry(() => import("./pages/ImagingLayouts"));
const EasternLayouts = lazyRetry(() => import("./pages/EasternLayouts"));
const ConditionPage = lazyRetry(() => import("./pages/ConditionPage"));
const ConditionEntry = lazyRetry(() => import("./pages/ConditionWorkspace"));
// The Honest Guide — open community health education Q&A (lives at /event).
const HonestGuidePage = lazyRetry(() => import("./pages/HonestGuidePage"));
const ReasoningPilotV2 = lazyRetry(() => import("./pages/ReasoningPilotV2"));
const ClinicalReasoningSurvey = lazyRetry(() => import("./pages/ClinicalReasoningSurvey"));
const MegaAdmin = lazyRetry(() => import("./pages/MegaAdmin"));
const LabReference = lazyRetry(() => import("./pages/LabReference"));
const ConditionsReference = lazyRetry(() => import("./pages/ConditionsReference"));
const Community = lazyRetry(() => import("./pages/Community"));
const ProofDashboard = lazyRetry(() => import("./pages/ProofDashboard"));
const StartPilot = lazyRetry(() => import("./pages/StartPilot"));
const MagicLogin = lazyRetry(() => import("./pages/MagicLogin"));
const InstitutionAdmin = lazyRetry(() => import("./pages/InstitutionAdmin"));
const Student = lazyRetry(() => import("./pages/Student"));
const SurveyResults = lazyRetry(() => import("./pages/SurveyResults"));
const SafetyResources = lazyRetry(() => import("./pages/SafetyResources"));
const Privacy  = lazyRetry(() => import("./pages/Legal").then((m) => ({ default: m.Privacy })));
const Terms    = lazyRetry(() => import("./pages/Legal").then((m) => ({ default: m.Terms })));
const Cookies  = lazyRetry(() => import("./pages/Legal").then((m) => ({ default: m.Cookies })));
// Explain ships THREE named exports plus default — wrap each in its own
// lazy factory pointing at the same chunk so the bundler co-locates them.
const Explain = lazyRetry(() => import("./pages/Explain"));
const ExplainSystem = lazyRetry(() => import("./pages/Explain").then((m) => ({ default: m.ExplainSystem })));
const ExplainDisease = lazyRetry(() => import("./pages/Explain").then((m) => ({ default: m.ExplainDisease })));
const ExplainScope = lazyRetry(() => import("./pages/Explain").then((m) => ({ default: m.ExplainScope })));
const Pathophysiology = lazyRetry(() => import("./pages/Pathophysiology"));
const AdminPilot = lazyRetry(() => import("./pages/AdminPilot"));
const AdminVenues = lazyRetry(() => import("./pages/AdminVenues"));
const AdminMismatches = lazyRetry(() => import("./pages/AdminMismatches"));
const AdminCognition = lazyRetry(() => import("./pages/AdminCognition"));
const PilotLanding = lazyRetry(() => import("./pages/pilot/PilotLanding"));
const PilotStudent = lazyRetry(() => import("./pages/pilot/PilotStudent"));
const PilotCommunity = lazyRetry(() => import("./pages/pilot/PilotCommunity"));
const PilotAdmin = lazyRetry(() => import("./pages/pilot/PilotAdmin"));
const PilotReport = lazyRetry(() => import("./pages/pilot/PilotReport"));
const PilotExecute = lazyRetry(() => import("./pages/pilot/PilotExecute"));
const PilotPreview = lazyRetry(() => import("./pages/pilot/PilotPreview"));
const PilotExecuteReport = lazyRetry(() => import("./pages/pilot/PilotExecuteReport"));
const PilotCohortReport = lazyRetry(() => import("./pages/pilot/PilotCohortReport"));
const FounderConsole = lazyRetry(() => import("./pages/FounderConsole"));
const Curriculum = lazyRetry(() => import("./pages/Curriculum"));
const CulturalCooking = lazyRetry(() => import("./pages/CulturalCooking"));
const HouseholdSim = lazyRetry(() => import("./pages/HouseholdSim"));
const HouseholdSimRun = lazyRetry(() => import("./pages/HouseholdSimRun"));
const ImagingProcedures = lazyRetry(() => import("./pages/ImagingProcedures"));
const SpecialtyImagingQuiz = lazyRetry(() => import("./pages/SpecialtyImagingQuiz"));
const TutorChat = lazyRetry(() => import("./pages/TutorChat"));
const TopicWorkspace = lazyRetry(() => import("./pages/TopicWorkspace"));
const ExplainShortPage = lazyRetry(() => import("./pages/ModulePages").then((m) => ({ default: m.ExplainShortPage })));
const ExplainLongPage = lazyRetry(() => import("./pages/ModulePages").then((m) => ({ default: m.ExplainLongPage })));
const SymptomsPage = lazyRetry(() => import("./pages/ModulePages").then((m) => ({ default: m.SymptomsPage })));
const EasternPage = lazyRetry(() => import("./pages/ModulePages").then((m) => ({ default: m.EasternPage })));
const AdminAudit = lazyRetry(() => import("./pages/AdminAudit"));
const OrgSignup = lazyRetry(() => import("./pages/OrgSignup"));
const OrgDashboard = lazyRetry(() => import("./pages/OrgDashboard"));
const OrgAdmin = lazyRetry(() => import("./pages/OrgAdmin"));
const CohortAnalytics = lazyRetry(() => import("./pages/CohortAnalytics"));
const LikertAssessment = lazyRetry(() => import("./pages/LikertAssessment"));
const SynthesisPage = lazyRetry(() => import("./pages/SynthesisPage"));
const CeHub = lazyRetry(() => import("./pages/CeHub"));
const CnaAdmin = lazyRetry(() => import("./pages/CnaAdmin"));
const BlsAcademy = lazyRetry(() => import("./pages/BlsAcademy"));
const AclsAcademy = lazyRetry(() => import("./pages/AclsAcademy"));
const BlsAdmin = lazyRetry(() => import("./pages/BlsAdmin"));
const CohortReport = lazyRetry(() => import("./pages/CohortReport"));
const EmployerDashboard = lazyRetry(() => import("./pages/EmployerDashboard"));
const ConditionCatalogAdmin = lazyRetry(() => import("./pages/ConditionCatalogAdmin"));
const MedLibrary = lazyRetry(() => import("./pages/MedLibrary"));
const ContentHealth = lazyRetry(() => import("./pages/ContentHealth"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const ModuleStatus = lazyRetry(() => import("./pages/ModuleStatus"));
const BillingReturn = lazyRetry(() => import("./pages/BillingReturn"));
import AnalyticsConsentBanner from "./components/AnalyticsConsentBanner";
import CookieConsent from "./components/CookieConsent";
// NeuralDock floating navigation removed per user request — header nav is sole entry point.
// RedFlags is no longer routed directly; its Forward/Reverse modes are
// composed inside ConditionsReference.
import "./pages/RedFlags";
import { AuthProvider } from "./lib/auth";
import { AudienceProvider } from "./lib/audience";
import { DepthProvider } from "./lib/depth";
import { TrackProvider, useTrackGuard } from "./lib/track";
import { EngagementProvider } from "./lib/engagement";
// TrackToggle (bottom-left academy shortcut stack) removed per founder
// direction — audiences switch via nav + module directory instead.
import { CommunityWrap } from "./components/CommunityShell";
import CommunityHowItWorks from "./pages/community/CommunityHowItWorks";
import CommunityFacilities from "./pages/community/CommunityFacilities";
import CommunityAnalytics from "./pages/community/CommunityAnalytics";
import CommunityPartnerReports from "./pages/community/CommunityPartnerReports";
const CommunityTutor = lazyRetry(() => import("./pages/community/CommunityTutor"));
const VisitPrep = lazyRetry(() => import("./pages/community/VisitPrep"));
const VisitSummary = lazyRetry(() => import("./pages/community/VisitSummary"));
const CaregiverPage = lazyRetry(() => import("./pages/community/Caregiver"));
const ResultsExplainer = lazyRetry(() => import("./pages/community/ResultsExplainer"));
const MedsExplainer = lazyRetry(() => import("./pages/community/MedsExplainer"));
const ProceduresExplainer = lazyRetry(() => import("./pages/community/ProceduresExplainer"));
const SchoolsLanding = lazyRetry(() => import("./pages/schools/SchoolsLanding"));
const SchoolsOnboarding = lazyRetry(() => import("./pages/schools/SchoolsOnboarding"));
const SchoolsHome = lazyRetry(() => import("./pages/schools/SchoolsHome"));
const SchoolsCases = lazyRetry(() => import("./pages/schools/SchoolsCases"));
const SchoolsCaseEngine = lazyRetry(() => import("./pages/schools/SchoolsCaseEngine"));
const SchoolsCareers = lazyRetry(() => import("./pages/schools/SchoolsCareers"));
const SchoolsCareerPage = lazyRetry(() => import("./pages/schools/SchoolsCareerPage"));
const SchoolsSkills = lazyRetry(() => import("./pages/schools/SchoolsSkills"));
const SchoolsSkillPlayer = lazyRetry(() => import("./pages/schools/SchoolsSkillPlayer"));
const SchoolsHealth = lazyRetry(() => import("./pages/schools/SchoolsHealth"));
const SchoolsHealthModule = lazyRetry(() => import("./pages/schools/SchoolsHealthModule"));
const SchoolsPresent = lazyRetry(() => import("./pages/schools/SchoolsPresent"));
const SchoolsPresentLab = lazyRetry(() => import("./pages/schools/SchoolsPresentLab"));
const SchoolsJoin = lazyRetry(() => import("./pages/schools/SchoolsJoin"));
const SchoolsTeach = lazyRetry(() => import("./pages/schools/SchoolsTeach"));
const SchoolsCertificate = lazyRetry(() => import("./pages/schools/SchoolsCertificate"));
const SchoolsAbout = lazyRetry(() => import("./pages/schools/SchoolsAbout"));
const SchoolsSystems = lazyRetry(() => import("./pages/schools/SchoolsSystems"));
const SchoolsSystemsChallenge = lazyRetry(() => import("./pages/schools/SchoolsSystemsChallenge"));
const SchoolsPortfolio = lazyRetry(() => import("./pages/schools/SchoolsPortfolio"));
const SchoolsTimeMachine = lazyRetry(() => import("./pages/schools/SchoolsTimeMachine"));
const SchoolsResources = lazyRetry(() => import("./pages/schools/SchoolsResources"));
const SchoolsProjects = lazyRetry(() => import("./pages/schools/SchoolsProjects"));
const SchoolsProjectPlayer = lazyRetry(() => import("./pages/schools/SchoolsProjectPlayer"));

// /community-health/* is a permanent alias of /community/* (founder PRD).
function CommunityHealthAlias() {
  const rest = useParams()["*"] || "";
  return <Navigate to={`/community/${rest}`} replace />;
}
import PatientUpdateToast from "./components/PatientUpdateToast";
import ClinicalDisclaimer from "./components/ClinicalDisclaimer";
import TrustFooter from "./components/TrustFooter";
import GlobalPdfFab from "./components/GlobalPdfFab";
import OfflineBanner from "./components/OfflineBanner";
import InstallAppPrompt from "./components/InstallAppPrompt";

/** Minimal Suspense fallback — a single shimmer bar that respects the
 *  brightened palette. Routes typically resolve in <300ms so the user
 *  rarely sees this. */
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="route-fallback">
      <div className="w-full max-w-md flex flex-col items-center">
        <img
          src="/brand/icon.png"
          alt="Maldek"
          className="w-16 h-16 rounded-2xl mb-6 brand-pulse"
          data-testid="route-fallback-logo"
        />
        <div className="w-full space-y-3">
          <div className="h-2 rounded-full bg-white/[0.06] pathophys-shimmer mx-auto" style={{ width: "65%" }} />
          <div className="h-2 rounded-full bg-white/[0.06] pathophys-shimmer mx-auto" style={{ width: "92%" }} />
          <div className="h-2 rounded-full bg-white/[0.06] pathophys-shimmer mx-auto" style={{ width: "78%" }} />
        </div>
      </div>
      <style>{`
        @keyframes brandPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.65; transform: scale(0.96); }
        }
        .brand-pulse { animation: brandPulse 1.6s ease-in-out infinite; }
        @keyframes pathophysShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .pathophys-shimmer {
          background-image: linear-gradient(
            90deg,
            rgba(255,255,255,0.04) 0%,
            rgba(107,143,113,0.18) 50%,
            rgba(255,255,255,0.04) 100%
          );
          background-size: 200% 100%;
          animation: pathophysShimmer 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

/** Slug-preserving redirects from the retired /pathophys pages to the merged
 *  /explain-long view. The patho story is now rendered inline inside the
 *  ExplainLong page (section 2b), so /pathophys/foo → /explain-long/foo. */
function NavigatePathophys() {
  const { slug = "" } = useParams();
  return <Navigate to={slug ? `/explain-long/${slug}` : "/explain-long"} replace />;
}
function NavigatePathophysWorkspace() {
  const { slug = "" } = useParams();
  return <Navigate to={slug ? `/explain-long/${slug}` : "/explain-long"} replace />;
}

/** Full Conditions removed from the community — deep links land on Explain Short. */
function CommunityExplainRedirect() {
  const { slug = "" } = useParams();
  return <Navigate to={slug ? `/explain-short/${slug}` : "/explain-short"} replace />;
}

/** Retired /medications + /meds-workspace → static Med Library.
 *  Preserves ?q= search deep links (e.g. /medications?q=SSRI). */
function NavigateMedLibrary() {
  const { search } = useLocation();
  const { slug } = useParams();
  return <Navigate to={slug ? `/med-library/drug/${slug}${search || ""}` : `/med-library${search || ""}`} replace />;
}


function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  // CRITICAL: synchronously route Emergent OAuth callback before normal routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Track wall — redirects off-track URLs to / where the umbrella hub
  // surfaces the "switch track" prompt.
  return <TrackGuardedRoutes location={location} navigate={navigate} />;
}

// Student-site uniform typography (all Fraunces) — every surface EXCEPT the
// community, schools, honest-truth, academy and admin/faculty areas.
const SERIF_EXCLUDED_PREFIXES = [
  "/community", "/community-health", "/explain-short", "/explain-short-layouts",
  "/community-library-layouts", "/schools", "/event",
  "/cna-academy", "/bls-academy", "/acls-academy", "/cna-admin", "/bls-admin",
  "/employer", "/maldek-admin", "/catalog-admin", "/content-health",
  "/admin", "/analytics", "/orgs",
];

function TrackGuardedRoutes({ location, navigate }) {
  useTrackGuard(navigate, location.pathname);
  useEffect(() => {
    const p = location.pathname;
    const excluded = SERIF_EXCLUDED_PREFIXES.some((pre) => p === pre || p.startsWith(pre + "/"));
    document.body.classList.toggle("maldek-serif", !excluded);
  }, [location.pathname]);
  // Header visibility is path-driven only (see `pathHidden` below). The
  // previous track-based suppression was removed because it killed the
  // global header on the home page / library / dashboards for any user
  // who had picked the CNA or BLS track.
  // Unknown-path catch: any first path segment NOT in this whitelist is
  // redirected to / so bogus URLs never strand unauth users on the sign-in
  // modal. Mirrors every <Route> registered below.
  const KNOWN_FIRST_SEGMENTS = new Set([
    "", "system", "solutions", "consulting", "education", "platform",
    "workforce-training", "community-health", "case-studies", "maldek-admin",
    "schools",
    "acls-academy",
    "about-maldek", "contact", "legacy-landing", "sign-in", "admin",
    "workforce", "cme", "business-plan", "community", "community-pilot", "clinical-pilot", "start-pilot",
    "mystery", "analytics", "theme-options", "modules", "event",
    "institution-admin", "household-sim", "imaging-procedures", "imaging-quiz", "synth", "tutor",
    "student", "ce-hub", "cna-academy", "cna-admin", "employer",
    "bls-admin", "bls-academy", "acls-academy", "clinician", "survey-results",
    "for-hospitals", "for-schools", "how-it-works", "concept", "reasoning-engine",
    "lab-reference", "explain", "pathophys", "pathophysiology", "red-flags",
    "conditions-reference", "medications-reference", "pathobridge",
    "reverse", "walkthrough", "clinical-reasoning", "clinical-reasoning-survey",
    "reasoning-pilot", "student-pilot", "educator-demonstration", "symptoms", "medications",
    "therapeutics", "dual-pathway", "clinical-brief", "supplements",
    "reasoning", "start", "results", "shift", "simulator", "cogsim", "time-machine",
    "compare", "patient", "practice-visit", "nclex", "__master",
    "for-institutions", "safety-net", "login", "dashboard", "cohorts",
    "cohort", "pilot", "curriculum", "cooking", "safety", "privacy",
    // iter120+: production auth flows (Phase 2 — verify, reset, invites)
    "verify-email", "forgot-password", "reset-password", "accept-invite",
    "terms", "cookies", "__founder", "maldek-admin", "clinical-library",
    // iter86+: unified Knowledge Workspace + per-module pages
    "topic",
    "explain-short", "explain-short-layouts", "explain-long", "explain-long-layouts", "community-library-layouts", "imaging-layouts", "eastern-layouts", "symptoms-workspace", "pathophys-workspace",
    "meds-workspace", "eastern-workspace",
    // Condition Catalog CMS (master-code gated).
    "catalog-admin",
    // Static Medication + Body Systems Library.
    "med-library",
    // Content Health audit dashboard (master-code gated).
    "content-health",
    // Pricing + Stripe checkout return.
    "pricing", "billing",
    // iter114+: self-serve multi-tenant org signup + dashboard
    "orgs",
    // iter110: Pediatric Master Study Map (RN + FNP lifespan)
    "pediatrics",
    // Module Health Registry status board.
    "module-status",
  ]);
  const firstSeg = location.pathname.split("/")[1] || "";
  if (!KNOWN_FIRST_SEGMENTS.has(firstSeg)) {
    return <Navigate to="/" replace />;
  }
  // Hide the global header on every walled-garden academy surface,
  // BEFORE and AFTER the gate is unlocked, and even for admin-unlocked
  // users. Founder direction: "i dont even want the top row to be seen by
  // cna academy or bls/acls academy."
  const HEADER_HIDDEN_PREFIXES = [
    "/cna-academy", "/bls-academy", "/acls-academy",
    "/cna-admin", "/bls-admin",
    "/employer",
    // iter116c — community-voice Explain · Short ships with its own
    // warm magazine chrome; the dark legacy header would clash badly.
    "/explain-short",
    "/explain-short-layouts",
    "/explain-long-layouts",
    "/community-library-layouts",
    "/imaging-layouts",
    "/eastern-layouts",
    // Community is a fully standalone, welcoming experience with its own
    // top bar + module strip — suppress the global student chrome.
    "/community",
    // Maldek Schools — standalone K-12 experience with its own top bar.
    "/schools",
    // Community Pilot Demonstration — standalone dark marketing page.
    "/community-pilot",
    // Clinical Reasoning Pilot Demonstration — standalone educator demo page.
    "/clinical-pilot",
    "/educator-demonstration",
    // Clinical Mystery Method — immersive case player + hub ship their own chrome.
    "/mystery",
    // CR 2.0 Medical Command Center — condition workspaces ship their own chrome.
    "/clinical-reasoning/condition",
    // Faculty Analytics dashboard — same cream chrome as the mystery suite.
    "/analytics",
    // Event Mode — live facilitated sessions ship their own chrome.
    "/event",
    // Theme palette showroom for the founder.
    "/theme-options",
  ];
  // Consulting marketing surfaces ship with their own header + footer
  // (MaldekConsultingLayout). Suppress the legacy global chrome there.
  // Consulting marketing surfaces + the walled-garden academies/admin all
  // ship their own chrome. Suppress the legacy global header/footer/banners
  // on those routes.
  const CONSULTING_MARKETING_PATHS = new Set([
    "/maldek-admin", "/maldek-admin/clinical-content", "/acls-academy",
    "/catalog-admin", "/content-health",
  ]);
  const onConsultingMarketing = CONSULTING_MARKETING_PATHS.has(location.pathname);
  const pathHidden = HEADER_HIDDEN_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  ) || onConsultingMarketing;
  // Header is suppressed ONLY on the academy / admin walled-garden surfaces
  // listed above. The previous track-based gate ("if track is cna/bls hide
  // globally") was over-eager — it killed the global header on the home
  // page, library, dashboards, etc. for any user who had picked the CNA or
  // BLS track. Per founder: the global header must appear on every screen
  // except the academy pages themselves.
  // The new philosophy-first homepage (MaldekHome) ships its own unified
  // premium nav — suppress the legacy global header/track-toggle on "/".
  const onNewHome = location.pathname === "/";
  const onPilotDemo = location.pathname === "/clinical-pilot";
  const onMystery = location.pathname === "/mystery" || location.pathname.startsWith("/mystery/") || location.pathname === "/analytics" || location.pathname === "/theme-options";
  const onCommunity = location.pathname === "/community" || location.pathname.startsWith("/community/");
  const hideHeader = pathHidden || onNewHome;
  return (
    <>
      {!hideHeader && (
        <div className="no-print">
          <Header />
        </div>
      )}
      {/* Print-only banner so saved PDFs are clearly branded. */}
      <div className="print-only" aria-hidden="true">
        <p style={{
          fontFamily: "JetBrains Mono, monospace",
          fontSize: "9pt",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "#6B8F71",
          margin: "0 0 6pt 0",
        }}>
          Maldek · Clinical Thinking Improvement System
        </p>
        <hr style={{ border: 0, borderTop: "0.5pt solid #e6f1fb", margin: "0 0 12pt 0" }} />
      </div>
      <Suspense fallback={<RouteFallback />}>
      {/* Identity wall — every route requires a signed-in identity
          EXCEPT the public marketing landing and the sign-in form
          itself. /reasoning-pilot used to bypass (it has its own
          master-code gate); we now stack identity *and* the master
          code so signing out drops you out of analytics too. */}
      {/* Public marketing routes (consulting site) — no RequireIdentity.
          /admin keeps its own gate; /sign-in is the auth surface itself.
          iter77+: the 10 student/community learning modules are now fully
          public per founder direction (Feb 21, 2026) — Explain, Symptoms,
          Labs, Meds, Imaging/Procedures, Therapeutics (Eastern), Cooking,
          Street History, Safety-Net (Resources), Household Sim, plus
          Pathophys (mechanism explainer) and /walkthrough (Explain·short).
          Their dynamic sub-routes (/explain/disease/:slug etc.) are
          handled via startsWith() checks below. */}
      {(() => {
        const p = location.pathname;
        const exact = new Set([
          "/", "/system", "/solutions", "/consulting", "/education",
          "/platform", "/workforce-training", "/community-health", "/case-studies",
          "/maldek-admin", "/maldek-admin/clinical-content",
          "/acls-academy", "/bls-academy", "/cna-academy", "/about-maldek", "/contact", "/legacy-landing",
          "/concept", "/how-it-works",
          // Marketing pages for prospective schools / institutions / hospitals
          // MUST be public — prospects visit these signed-out.
          "/for-schools", "/for-institutions", "/for-hospitals",
          // Maldek Schools (K-12) — public student experience.
          "/schools",
          "/sign-in", "/admin", "/admin/audit", "/clinical-library", "/clinical-reasoning",
          // Phase-1 auth flows (Feb 2026) — public so the verification,
          // reset, and invite emails work for unauthenticated users.
          "/login", "/verify-email", "/forgot-password", "/reset-password",
          "/accept-invite",
          // iter114 — public self-serve org signup + dashboard.
          "/orgs/signup", "/orgs/dashboard",
          // Condition Catalog CMS ships its own master-code gate.
          "/catalog-admin", "/content-health",
          // Public pricing page.
          "/pricing",
          // Phase 6 — public legal pages.
          "/privacy", "/terms",
          // iter77+: public learning modules
          "/community", "/community/proof", "/community-pilot", "/clinical-pilot", "/educator-demonstration", "/reasoning-pilot", "/mystery", "/analytics", "/event", "/theme-options", "/modules", "/explain-short-layouts", "/explain-long-layouts", "/community-library-layouts", "/imaging-layouts", "/eastern-layouts",
          "/explain", "/walkthrough", "/symptoms", "/lab-reference",
          "/medications", "/imaging-procedures", "/imaging-quiz", "/tutor", "/therapeutics", "/cooking",
          "/safety-net", "/household-sim",
          "/pathophys", "/pathophysiology",
          // iter80+: patient + NCLEX modules also public per founder direction
          "/patient", "/nclex",
          // Peds lifespan study map — public per founder direction (no sign-in).
          "/pediatrics",
          // Phase 5/6: paid sim made publicly routable so the LockedModule
          // upgrade-prompt renders for anonymous community users (vs. the
          // generic walled-garden gate). Privacy + Terms are pure legal.
          "/cogsim", "/time-machine", "/privacy", "/terms",
          // Therapeutics revamp: legacy URLs redirect to /therapeutics — keep them public.
          "/dual-pathway", "/supplements",
          // iter86+: unified Knowledge Workspace + per-module pages
          "/topic",
          "/explain-short", "/explain-long", "/symptoms-workspace",
          "/pathophys-workspace", "/meds-workspace", "/eastern-workspace",
          "/med-library",
          // Module Health Registry status board (public founder view).
          "/module-status",
        ]);
        const prefixes = [
          "/explain/", "/clinical-library/",
          "/community/",
          "/community-health/",
          "/schools/",
          // iter80c+: /synth/:kind is the fallback surface Therapeutics
          // and Meds route to when a drug isn't in the static catalog.
          // Must be public so the library-link fallback is reachable
          // when the LLM is over budget.
          "/synth/",
          // iter86+: unified Knowledge Workspace + per-module pages
          "/topic/", "/topic",
          "/explain-short/", "/explain-long/",
          "/symptoms-workspace/", "/pathophys-workspace/",
          "/meds-workspace/", "/eastern-workspace/",
          // iter88+ — learner modules MUST be public per the
          // "no auth gating on learning modules" PRD.
          "/symptoms/", "/pathophys/", "/medications/", "/therapeutics/",
          // iter114 — self-serve org dashboard with token gate.
          "/orgs/",
          // Phase 1 catalog (Feb 2026) — public browsing per PRD.
          "/clinical-reasoning/",
          // Static Medication Library — public learning module.
          "/med-library/",
          // Peds lifespan condition pages — public (no sign-in).
          "/pediatrics/",
          // Stripe checkout return (page handles auth via API).
          "/billing/",
          // Clinical Mystery Method — public signature cases.
          "/mystery/",
          // Event Mode — participants join with no account; facilitator
          // surfaces carry their own master-code gate.
          "/event/",
        ];
        return exact.has(p) || prefixes.some((pre) => p.startsWith(pre));
      })()
        ? (
          <Routes>
            <Route path="/" element={<MaldekHome />} />
            {/* Deprecated consulting marketing paths — redirect to product home. */}
            <Route path="/system" element={<Navigate to="/" replace />} />
            <Route path="/solutions" element={<Navigate to="/" replace />} />
            <Route path="/consulting" element={<Navigate to="/" replace />} />
            <Route path="/education" element={<Navigate to="/" replace />} />
            <Route path="/platform" element={<Navigate to="/clinical-reasoning" replace />} />
            <Route path="/workforce-training" element={<Navigate to="/" replace />} />
            <Route path="/community-health" element={<Navigate to="/community" replace />} />
            <Route path="/community-health/*" element={<CommunityHealthAlias />} />
            <Route path="/schools" element={<SchoolsLanding />} />
            <Route path="/schools/onboarding" element={<SchoolsOnboarding />} />
            <Route path="/schools/home" element={<SchoolsHome />} />
            <Route path="/schools/cases" element={<SchoolsCases />} />
            <Route path="/schools/cases/:slug" element={<SchoolsCaseEngine />} />
            <Route path="/schools/careers" element={<SchoolsCareers />} />
            <Route path="/schools/careers/:slug" element={<SchoolsCareerPage />} />
            <Route path="/schools/skills" element={<SchoolsSkills />} />
            <Route path="/schools/skills/:slug" element={<SchoolsSkillPlayer />} />
            <Route path="/schools/health" element={<SchoolsHealth />} />
            <Route path="/schools/health/:slug" element={<SchoolsHealthModule />} />
            <Route path="/schools/present" element={<SchoolsPresent />} />
            <Route path="/schools/present-lab" element={<SchoolsPresentLab />} />
            <Route path="/schools/join" element={<SchoolsJoin />} />
            <Route path="/schools/teach" element={<SchoolsTeach />} />
            <Route path="/schools/about" element={<SchoolsAbout />} />
            <Route path="/schools/systems" element={<SchoolsSystems />} />
            <Route path="/schools/systems/:slug" element={<SchoolsSystemsChallenge />} />
            <Route path="/schools/portfolio" element={<SchoolsPortfolio />} />
            <Route path="/schools/time-machine/:slug" element={<SchoolsTimeMachine />} />
            <Route path="/schools/resources" element={<SchoolsResources />} />
            <Route path="/schools/projects" element={<SchoolsProjects />} />
            <Route path="/schools/projects/:slug" element={<SchoolsProjectPlayer />} />
            <Route path="/schools/certificate/:slug" element={<SchoolsCertificate />} />
            <Route path="/case-studies" element={<Navigate to="/" replace />} />
            <Route path="/about-maldek" element={<Navigate to="/" replace />} />
            <Route path="/contact" element={<Navigate to="/" replace />} />
            <Route path="/maldek-admin" element={<MaldekAdminDashboard />} />
            <Route path="/maldek-admin/clinical-content" element={<ClinicalContentControl />} />
        <Route path="/catalog-admin" element={<ConditionCatalogAdmin />} />
            <Route path="/content-health" element={<ContentHealth />} />
            <Route path="/module-status" element={<ModuleStatus />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/concept" element={<Concept />} />
            <Route path="/for-schools" element={<ForSchools />} />
            <Route path="/for-institutions" element={<ForInstitutions />} />
            <Route path="/for-hospitals" element={<ForHospitals />} />
            <Route path="/billing/return" element={<BillingReturn />} />
            <Route path="/clinical-library" element={<Navigate to="/modules" replace />} />
            <Route path="/clinical-reasoning" element={<CatalogHome />} />
            <Route path="/clinical-reasoning/compare" element={<CompareConditions />} />
            <Route path="/clinical-reasoning/intro" element={<ClinicalReasoningIntro />} />
            <Route path="/clinical-reasoning/condition/:slug" element={<ConditionEntry />} />
            <Route path="/med-library" element={<MedLibrary />} />
            <Route path="/med-library/drug/:drugSlug" element={<MedLibrary />} />
            <Route path="/med-library/:system" element={<MedLibrary />} />
            <Route path="/med-library/:system/:classSlug" element={<MedLibrary />} />
            <Route path="/pediatrics" element={<Pediatrics />} />
            <Route path="/pediatrics/condition/:slug" element={<PediatricCondition />} />
            <Route path="/bls-academy" element={<BlsAcademy />} />
            <Route path="/acls-academy" element={<AclsAcademy />} />
            <Route path="/cna-academy" element={<Navigate to="/bls-academy" replace />} />
            <Route path="/legacy-landing" element={<LegacyLanding />} />
            <Route path="/sign-in" element={<MagicLogin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/admin" element={<MegaAdmin />} />
            <Route path="/admin/audit" element={<AdminAudit />} />
            {/* Self-serve multi-tenant org signup + dashboard. Both are
                public routes; the dashboard gates content with the
                Bearer admin token issued at signup. */}
            <Route path="/orgs/signup" element={<OrgSignup />} />
            <Route path="/orgs/admin" element={<OrgAdmin />} />
            <Route path="/orgs/:org_id/dashboard" element={<OrgDashboard />} />
            <Route path="/orgs/dashboard" element={<OrgDashboard />} />
            {/* iter86 — legacy module URLs now powered by the working
                KnowledgeRelease backend. Old components (Explain.jsx,
                Symptoms.jsx, etc.) remain on disk but are no longer routed. */}
            <Route path="/explain" element={<ExplainLongPage />} />
            <Route path="/explain/:slug" element={<ExplainLongPage />} />
            <Route path="/explain/disease/:slug" element={<ExplainLongPage />} />
            <Route path="/explain/scope/:scopeId" element={<ExplainScope />} />
            <Route path="/explain-short" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
            <Route path="/explain-short-layouts" element={<ExplainShortLayouts />} />
            <Route path="/explain-long-layouts" element={<ExplainLongLayouts />} />
            <Route path="/community-library-layouts" element={<CommunityLibraryLayouts />} />
            <Route path="/imaging-layouts" element={<ImagingLayouts />} />
            <Route path="/eastern-layouts" element={<EasternLayouts />} />
            <Route path="/explain-short/:slug" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
            <Route path="/walkthrough" element={<Walkthrough />} />
            <Route path="/symptoms" element={<SymptomsPage />} />
            <Route path="/symptoms/:slug" element={<SymptomsPage />} />
            <Route path="/lab-reference" element={<LabReference />} />
            <Route path="/medications" element={<NavigateMedLibrary />} />
            <Route path="/medications/:slug" element={<NavigateMedLibrary />} />
            <Route path="/imaging-procedures" element={<ImagingProcedures />} />
            <Route path="/imaging-quiz" element={<SpecialtyImagingQuiz />} />
            <Route path="/tutor" element={<TutorChat />} />
            <Route path="/community/tutor" element={<CommunityWrap><CommunityTutor /></CommunityWrap>} />
            <Route path="/therapeutics" element={<Therapeutics />} />
            <Route path="/therapeutics/:slug" element={<Therapeutics />} />
            <Route path="/dual-pathway" element={<Navigate to="/therapeutics" replace />} />
            <Route path="/supplements"  element={<Navigate to="/therapeutics" replace />} />
            <Route path="/cooking" element={<CulturalCooking />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community-pilot" element={<Navigate to="/community" replace />} />
            <Route path="/educator-demonstration" element={<Navigate to="/mystery" replace />} />
            <Route path="/clinical-pilot" element={<Navigate to="/mystery" replace />} />
            <Route path="/student-pilot" element={<ReasoningPilotV2 />} />
            <Route path="/reasoning-pilot" element={<Navigate to="/student-pilot" replace />} />
            <Route path="/mystery" element={<MysteryHub />} />
            <Route path="/mystery/:caseId" element={<MysteryCase />} />
            <Route path="/analytics" element={<FacultyAnalytics />} />
            <Route path="/theme-options" element={<ThemeOptions />} />
            <Route path="/modules" element={<ModulesDirectory />} />
            <Route path="/community/how-it-works" element={<CommunityHowItWorks />} />
            <Route path="/community/facilities" element={<CommunityFacilities />} />
            <Route path="/community/analytics" element={<CommunityAnalytics />} />
            <Route path="/community/partner-reports" element={<CommunityPartnerReports />} />
            <Route path="/community/proof" element={<ProofDashboard />} />
            <Route path="/community/explain" element={<Navigate to="/explain-short" replace />} />
            <Route path="/community/explain/:slug" element={<CommunityExplainRedirect />} />
            <Route path="/community/symptoms" element={<CommunityWrap><SymptomsPage /></CommunityWrap>} />
            <Route path="/community/symptoms/:slug" element={<CommunityWrap><SymptomsPage /></CommunityWrap>} />
            <Route path="/community/labs" element={<CommunityWrap><LabReference /></CommunityWrap>} />
            <Route path="/community/medications" element={<CommunityWrap><MedsExplainer /></CommunityWrap>} />
            <Route path="/community/procedures" element={<CommunityWrap><ProceduresExplainer /></CommunityWrap>} />
            <Route path="/community/cooking" element={<CommunityWrap><CulturalCooking /></CommunityWrap>} />
            <Route path="/community/resources" element={<CommunityWrap><SafetyNet /></CommunityWrap>} />
            <Route path="/community/results" element={<CommunityWrap><ResultsExplainer /></CommunityWrap>} />
            <Route path="/community/conditions" element={<Navigate to="/explain-short" replace />} />
            <Route path="/community/visit-prep" element={<CommunityWrap><VisitPrep /></CommunityWrap>} />
            <Route path="/community/visit-summary" element={<CommunityWrap><VisitSummary /></CommunityWrap>} />
            <Route path="/community/caregiver" element={<CommunityWrap><CaregiverPage /></CommunityWrap>} />
            <Route path="/community/household" element={<CommunityWrap><HouseholdSim /></CommunityWrap>} />
            <Route path="/safety-net" element={<SafetyNet />} />
            <Route path="/household-sim" element={<HouseholdSim />} />
            {/* /pathophys legacy routes — merged into /explain-long. Redirect
                preserves any deep links from bookmarks, share links, LMS embeds. */}
            <Route path="/pathophys" element={<Navigate to="/explain-long" replace />} />
            <Route path="/pathophys/:slug" element={<NavigatePathophys />} />
            <Route path="/pathophysiology" element={<Navigate to="/explain-long" replace />} />
            {/* iter86 — unified Knowledge Workspace (tabs) */}
            <Route path="/topic" element={<Navigate to="/modules" replace />} />
            <Route path="/topic/:slug" element={<TopicWorkspace />} />
            {/* iter86 — per-module pages (separate routes, no tabs) */}
            <Route path="/explain-short" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
            <Route path="/explain-short-layouts" element={<ExplainShortLayouts />} />
            <Route path="/explain-long-layouts" element={<ExplainLongLayouts />} />
            <Route path="/community-library-layouts" element={<CommunityLibraryLayouts />} />
            <Route path="/explain-short/:slug" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
            <Route path="/explain-long" element={<ExplainLongPage />} />
            <Route path="/explain-long/:slug" element={<ExplainLongPage />} />
            <Route path="/symptoms-workspace" element={<SymptomsPage />} />
            <Route path="/symptoms-workspace/:slug" element={<SymptomsPage />} />
            <Route path="/pathophys-workspace" element={<Navigate to="/explain-long" replace />} />
            <Route path="/pathophys-workspace/:slug" element={<NavigatePathophysWorkspace />} />
            <Route path="/meds-workspace" element={<Navigate to="/med-library" replace />} />
            <Route path="/meds-workspace/:slug" element={<Navigate to="/med-library" replace />} />
            <Route path="/eastern-workspace" element={<EasternPage />} />
            <Route path="/eastern-workspace/:slug" element={<EasternPage />} />
            <Route path="/clinical-reasoning" element={<CatalogHome />} />
            <Route path="/clinical-reasoning/intro" element={<ClinicalReasoningIntro />} />
            <Route path="/clinical-reasoning/workspace" element={<Navigate to="/clinical-reasoning" replace />} />
            <Route path="/clinical-reasoning/condition/:slug" element={<ConditionEntry />} />
            <Route path="/patient" element={<Patient />} />
            <Route path="/time-machine" element={<TimeMachine />} />
            <Route path="/cogsim" element={<Navigate to="/time-machine" replace />} />
            <Route path="/nclex" element={<Nclex />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            {/* iter80c+: /synth/:kind made public so the topic-release
                fallback is reachable when live LLM is over budget. */}
            <Route path="/synth/:kind" element={<SynthesisPage />} />
            {/* The Honest Guide — open community health education Q&A. */}
            <Route path="/event" element={<HonestGuidePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
        <RequireIdentity>
      <Routes>
        <Route path="/" element={<MaldekHome />} />
        {/* Deprecated consulting marketing paths — redirect to product home. */}
        <Route path="/system" element={<Navigate to="/" replace />} />
        <Route path="/solutions" element={<Navigate to="/" replace />} />
        <Route path="/consulting" element={<Navigate to="/" replace />} />
        <Route path="/education" element={<Navigate to="/" replace />} />
        <Route path="/platform" element={<Navigate to="/clinical-reasoning" replace />} />
        <Route path="/workforce-training" element={<Navigate to="/" replace />} />
        <Route path="/community-health" element={<Navigate to="/community" replace />} />
        <Route path="/community-health/*" element={<CommunityHealthAlias />} />
        <Route path="/schools" element={<SchoolsLanding />} />
        <Route path="/schools/onboarding" element={<SchoolsOnboarding />} />
        <Route path="/schools/home" element={<SchoolsHome />} />
        <Route path="/schools/cases" element={<SchoolsCases />} />
        <Route path="/schools/cases/:slug" element={<SchoolsCaseEngine />} />
        <Route path="/schools/careers" element={<SchoolsCareers />} />
        <Route path="/schools/careers/:slug" element={<SchoolsCareerPage />} />
        <Route path="/schools/skills" element={<SchoolsSkills />} />
        <Route path="/schools/skills/:slug" element={<SchoolsSkillPlayer />} />
        <Route path="/schools/health" element={<SchoolsHealth />} />
        <Route path="/schools/health/:slug" element={<SchoolsHealthModule />} />
        <Route path="/schools/present" element={<SchoolsPresent />} />
        <Route path="/schools/present-lab" element={<SchoolsPresentLab />} />
        <Route path="/schools/join" element={<SchoolsJoin />} />
        <Route path="/schools/teach" element={<SchoolsTeach />} />
        <Route path="/schools/about" element={<SchoolsAbout />} />
        <Route path="/schools/systems" element={<SchoolsSystems />} />
        <Route path="/schools/systems/:slug" element={<SchoolsSystemsChallenge />} />
        <Route path="/schools/portfolio" element={<SchoolsPortfolio />} />
        <Route path="/schools/time-machine/:slug" element={<SchoolsTimeMachine />} />
        <Route path="/schools/resources" element={<SchoolsResources />} />
        <Route path="/schools/projects" element={<SchoolsProjects />} />
        <Route path="/schools/projects/:slug" element={<SchoolsProjectPlayer />} />
        <Route path="/schools/certificate/:slug" element={<SchoolsCertificate />} />
        <Route path="/case-studies" element={<Navigate to="/" replace />} />
        <Route path="/maldek-admin" element={<MaldekAdminDashboard />} />
        <Route path="/maldek-admin/clinical-content" element={<ClinicalContentControl />} />
        <Route path="/catalog-admin" element={<ConditionCatalogAdmin />} />
        <Route path="/content-health" element={<ContentHealth />} />
        <Route path="/module-status" element={<ModuleStatus />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing/return" element={<BillingReturn />} />
        <Route path="/med-library" element={<MedLibrary />} />
        <Route path="/med-library/drug/:drugSlug" element={<MedLibrary />} />
        <Route path="/med-library/:system" element={<MedLibrary />} />
        <Route path="/med-library/:system/:classSlug" element={<MedLibrary />} />
        <Route path="/clinical-library" element={<Navigate to="/modules" replace />} />
        <Route path="/pediatrics" element={<Pediatrics />} />
        <Route path="/pediatrics/condition/:slug" element={<PediatricCondition />} />
        <Route path="/contact" element={<Navigate to="/" replace />} />
        <Route path="/legacy-landing" element={<LegacyLanding />} />
        <Route path="/workforce" element={<WorkforcePipeline />} />
        <Route path="/cme" element={<Cme />} />
        <Route path="/business-plan" element={<BusinessPlan />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community-pilot" element={<Navigate to="/community" replace />} />
        <Route path="/educator-demonstration" element={<Navigate to="/mystery" replace />} />
        <Route path="/clinical-pilot" element={<Navigate to="/mystery" replace />} />
        <Route path="/mystery" element={<MysteryHub />} />
        <Route path="/mystery/:caseId" element={<MysteryCase />} />
        <Route path="/analytics" element={<FacultyAnalytics />} />
        <Route path="/theme-options" element={<ThemeOptions />} />
        <Route path="/modules" element={<ModulesDirectory />} />
        <Route path="/community/how-it-works" element={<CommunityHowItWorks />} />
        <Route path="/community/facilities" element={<CommunityFacilities />} />
        <Route path="/community/analytics" element={<CommunityAnalytics />} />
        <Route path="/community/partner-reports" element={<CommunityPartnerReports />} />
        <Route path="/community/proof" element={<ProofDashboard />} />
        <Route path="/community/explain" element={<Navigate to="/explain-short" replace />} />
        <Route path="/community/explain/:slug" element={<CommunityExplainRedirect />} />
        <Route path="/community/symptoms" element={<CommunityWrap><SymptomsPage /></CommunityWrap>} />
        <Route path="/community/symptoms/:slug" element={<CommunityWrap><SymptomsPage /></CommunityWrap>} />
        <Route path="/community/labs" element={<CommunityWrap><LabReference /></CommunityWrap>} />
        <Route path="/community/medications" element={<CommunityWrap><MedsExplainer /></CommunityWrap>} />
        <Route path="/community/procedures" element={<CommunityWrap><ProceduresExplainer /></CommunityWrap>} />
        <Route path="/community/cooking" element={<CommunityWrap><CulturalCooking /></CommunityWrap>} />
        <Route path="/community/resources" element={<CommunityWrap><SafetyNet /></CommunityWrap>} />
        <Route path="/community/results" element={<CommunityWrap><ResultsExplainer /></CommunityWrap>} />
        <Route path="/community/conditions" element={<Navigate to="/explain-short" replace />} />
        <Route path="/community/visit-prep" element={<CommunityWrap><VisitPrep /></CommunityWrap>} />
        <Route path="/community/visit-summary" element={<CommunityWrap><VisitSummary /></CommunityWrap>} />
        <Route path="/community/caregiver" element={<CommunityWrap><CaregiverPage /></CommunityWrap>} />
        <Route path="/community/household" element={<CommunityWrap><HouseholdSim /></CommunityWrap>} />
        <Route path="/start-pilot" element={<StartPilot />} />
        <Route path="/sign-in" element={<MagicLogin />} />
        <Route path="/institution-admin" element={<InstitutionAdmin />} />
        <Route path="/household-sim" element={<HouseholdSim />} />
        <Route path="/imaging-procedures" element={<ImagingProcedures />} />
        <Route path="/imaging-quiz" element={<SpecialtyImagingQuiz />} />
        <Route path="/tutor" element={<TutorChat />} />
        <Route path="/community/tutor" element={<CommunityWrap><CommunityTutor /></CommunityWrap>} />
        <Route path="/synth/:kind" element={<SynthesisPage />} />
        <Route path="/student" element={<Student />} />
        <Route path="/ce-hub" element={<CeHub />} />
        <Route path="/cna-academy" element={<Navigate to="/bls-academy" replace />} />
        <Route path="/cna-admin" element={<CnaAdmin />} />
        <Route path="/cna-admin/cohort/:id/report" element={<CohortReport />} />
        <Route path="/employer" element={<EmployerDashboard />} />
        <Route path="/bls-admin" element={<BlsAdmin />} />
        <Route path="/bls-academy" element={<BlsAcademy />} />
        <Route path="/acls-academy" element={<AclsAcademy />} />
        <Route path="/clinician" element={<Navigate to="/student" replace />} />
        <Route path="/survey-results" element={<SurveyResults />} />
        <Route path="/for-hospitals" element={<ForHospitals />} />
        <Route path="/for-schools" element={<ForSchools />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/concept" element={<Concept />} />
        <Route path="/reasoning-engine" element={<Navigate to="/clinical-reasoning" replace />} />
        <Route path="/lab-reference" element={<LabReference />} />
        <Route path="/explain" element={<ExplainLongPage />} />
        <Route path="/explain/:slug" element={<ExplainLongPage />} />
        <Route path="/explain/disease/:slug" element={<ExplainLongPage />} />
        <Route path="/explain/scope/:scopeId" element={<ExplainScope />} />
        <Route path="/explain-short" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
        <Route path="/explain-short-layouts" element={<ExplainShortLayouts />} />
        <Route path="/explain-long-layouts" element={<ExplainLongLayouts />} />
        <Route path="/explain-short/:slug" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
        <Route path="/pathophys" element={<Navigate to="/explain-long" replace />} />
        <Route path="/pathophys/:slug" element={<NavigatePathophys />} />
        <Route path="/pathophysiology" element={<Navigate to="/explain-long" replace />} />
        <Route path="/red-flags" element={<Navigate to="/symptoms?tab=forward" replace />} />
        <Route path="/red-flags/forward" element={<Navigate to="/symptoms?tab=forward" replace />} />
        <Route path="/red-flags/reverse" element={<Navigate to="/symptoms" replace />} />
        {/* iter86 — unified Knowledge Workspace (tabs) */}
        <Route path="/topic" element={<Navigate to="/modules" replace />} />
        <Route path="/topic/:slug" element={<TopicWorkspace />} />
        {/* iter86 — per-module pages (separate routes, no tabs) */}
        <Route path="/explain-short" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
        <Route path="/explain-short-layouts" element={<ExplainShortLayouts />} />
        <Route path="/explain-long-layouts" element={<ExplainLongLayouts />} />
        <Route path="/explain-short/:slug" element={<CommunityWrap><ExplainShortPage /></CommunityWrap>} />
        <Route path="/explain-long" element={<ExplainLongPage />} />
        <Route path="/explain-long/:slug" element={<ExplainLongPage />} />
        <Route path="/symptoms-workspace" element={<SymptomsPage />} />
        <Route path="/symptoms-workspace/:slug" element={<SymptomsPage />} />
        <Route path="/pathophys-workspace" element={<Navigate to="/explain-long" replace />} />
        <Route path="/pathophys-workspace/:slug" element={<NavigatePathophysWorkspace />} />
        <Route path="/meds-workspace" element={<Navigate to="/med-library" replace />} />
        <Route path="/meds-workspace/:slug" element={<Navigate to="/med-library" replace />} />
        <Route path="/eastern-workspace" element={<EasternPage />} />
        <Route path="/eastern-workspace/:slug" element={<EasternPage />} />
        <Route path="/conditions-reference" element={<Navigate to="/walkthrough?tab=conditions" replace />} />
        <Route path="/explain/disease/:slug" element={<ExplainDisease />} />
        <Route path="/explain/scope/:scopeId" element={<ExplainScope />} />
        <Route path="/explain/:systemId" element={<ExplainSystem />} />
        <Route path="/medications-reference" element={<Navigate to="/med-library" replace />} />
        <Route path="/pathobridge" element={<Navigate to="/simulator" replace />} />
        <Route path="/reverse" element={<Navigate to="/symptoms" replace />} />
        <Route path="/walkthrough" element={<Walkthrough />} />
        <Route path="/clinical-reasoning" element={<CatalogHome />} />
        <Route path="/clinical-reasoning/compare" element={<CompareConditions />} />
        <Route path="/clinical-reasoning/intro" element={<ClinicalReasoningIntro />} />
        <Route path="/clinical-reasoning/workspace" element={<Navigate to="/clinical-reasoning" replace />} />
        <Route path="/clinical-reasoning/condition/:slug" element={<ConditionEntry />} />
        <Route path="/clinical-reasoning-survey" element={<ClinicalReasoningSurvey />} />
        <Route path="/student-pilot" element={<ReasoningPilotV2 />} />
        <Route path="/reasoning-pilot" element={<Navigate to="/student-pilot" replace />} />
        <Route path="/symptoms" element={<SymptomsPage />} />
        <Route path="/symptoms/:slug" element={<SymptomsPage />} />
        <Route path="/medications" element={<NavigateMedLibrary />} />
        <Route path="/medications/:slug" element={<NavigateMedLibrary />} />
        <Route path="/therapeutics" element={<Therapeutics />} />
        <Route path="/therapeutics/:slug" element={<Therapeutics />} />
        <Route path="/dual-pathway" element={<Navigate to="/therapeutics" replace />} />
        <Route path="/clinical-brief" element={<Navigate to="/explain" replace />} />
        <Route path="/supplements" element={<Navigate to="/therapeutics?tab=lookup" replace />} />
        <Route path="/reasoning" element={<Reasoning />} />
        <Route path="/start" element={<Start />} />
        {/* /results was retired Feb 2026 — analytics now lives at /reasoning-pilot.
            Keep a 301-style redirect so any external bookmarks still land. */}
        <Route path="/results" element={<Navigate to="/reasoning-pilot?view=dashboard" replace />} />
        <Route path="/shift" element={<Navigate to="/dashboard" replace />} />
        <Route path="/run-shift" element={<Navigate to="/dashboard" replace />} />
        <Route path="/simulator" element={<Navigate to="/time-machine" replace />} />
        <Route path="/time-machine" element={<TimeMachine />} />
        <Route path="/cogsim" element={<Navigate to="/time-machine" replace />} />
        <Route path="/compare" element={<Navigate to="/simulator?mode=compare" replace />} />
        <Route path="/patient" element={<Patient />} />
        <Route path="/practice-visit" element={<PracticeVisit />} />
        <Route path="/nclex" element={<Nclex />} />
        <Route path="/nclex/dashboard" element={<NclexDashboard />} />
        <Route path="/nclex/admin" element={<NclexAdmin />} />
        <Route path="/__master" element={<MasterDashboard />} />
        <Route path="/for-institutions" element={<ForInstitutions />} />
        <Route path="/safety-net" element={<SafetyNet />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/cohorts" element={<Cohorts />} />
        <Route path="/cohort/:id" element={<CohortDetail />} />
        <Route path="/cohort/:id/analytics"  element={<CohortAnalytics />} />
        <Route path="/cohort/:id/assessment" element={<LikertAssessment />} />
        <Route path="/orgs/admin" element={<OrgAdmin />} />
        <Route path="/admin/pilot" element={<AdminPilot />} />
        <Route path="/admin/venues" element={<AdminVenues />} />
        <Route path="/admin/mismatches" element={<AdminMismatches />} />
        <Route path="/admin/cognition" element={<AdminCognition />} />
        <Route path="/pilot" element={<PilotLanding />} />
        <Route path="/pilot/student" element={<PilotStudent />} />
        <Route path="/pilot/community" element={<PilotCommunity />} />
        <Route path="/pilot/admin" element={<PilotAdmin />} />
        <Route path="/pilot/report/:pilotId" element={<PilotReport />} />
        <Route path="/pilot/execute/:module" element={<PilotExecute />} />
        <Route path="/pilot/preview/:slug" element={<PilotPreview />} />
        <Route path="/pilot/exec-report/:executionId" element={<PilotExecuteReport />} />
        <Route path="/pilot/cohort-report/:cohortId" element={<PilotCohortReport />} />
        <Route path="/admin/founder" element={<FounderConsole />} />
        <Route path="/curriculum" element={<Curriculum />} />
        <Route path="/cooking" element={<CulturalCooking />} />
        <Route path="/safety" element={<SafetyResources />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/__founder/*" element={<Landing />} />
        {/* Catch-all — any unknown path bounces to the consulting home. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
        </RequireIdentity>
      )}
      </Suspense>
      <div className="no-print">
        {!onConsultingMarketing && !pathHidden && <TrustFooter />}
        <PatientUpdateToast />
        <LearnerIdPrompt />
        {!onConsultingMarketing && !pathHidden && <ClinicalDisclaimer />}
        {!onConsultingMarketing && !pathHidden && <AnalyticsConsentBanner />}
        {!onConsultingMarketing && !pathHidden && <CookieConsent />}
        <GlobalPdfFab />
        <OfflineBanner />
        <InstallAppPrompt />
      </div>
    </>
  );
}

function App() {
  useEffect(() => { warmTopEndpoints(); }, []);
  return (
    <div className="App bg-background min-h-screen text-foreground">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <AudienceProvider>
            <DepthProvider>
              <TrackProvider>
                <EngagementProvider>
                  <LangProvider>
                    <AppErrorBoundary>
                      <AppRouter />
                    </AppErrorBoundary>
                  </LangProvider>
                </EngagementProvider>
              </TrackProvider>
            </DepthProvider>
          </AudienceProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;

